import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { DesignMode, getDesignTokens } from './designTokens';

/**
 * GlassButton — the design system's "physical glass" button.
 *
 * Use it for dark, hero, and premium moments (over the space shader, dark
 * product bands, prize panels). For everyday light UI, use SiteButton instead.
 *
 * The glass look is built from:
 * - A curved specular highlight across the top half (like light on a lens)
 * - A bright refractive rim on the top edge, darker rim on the bottom edge
 * - Deep backdrop blur + saturation so content behind reads as refracted
 * - A sheen that sweeps across the surface on hover
 * - A subtle inner bottom glow, as if light pools at the base of the glass
 */

type GlassButtonTone = 'crystal' | 'aurora' | 'gold' | 'ruby' | 'obsidian';

export type GlassButtonProps = Omit<ButtonProps, 'color' | 'variant'> & {
  mode?: DesignMode;
  tone?: GlassButtonTone;
};

const toneStyles: Record<
  GlassButtonTone,
  {
    color: string;
    background: string;
    hoverBackground: string;
    rimTop: string;
    rimBottom: string;
    glow: string;
  }
> = {
  crystal: {
    color: '#ffffff',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 48%, rgba(255,255,255,0.14) 100%)',
    hoverBackground:
      'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.12) 48%, rgba(255,255,255,0.2) 100%)',
    rimTop: 'rgba(255,255,255,0.85)',
    rimBottom: 'rgba(255,255,255,0.25)',
    glow: 'rgba(255,255,255,0.35)'
  },
  aurora: {
    color: '#eafffb',
    background:
      'linear-gradient(180deg, rgba(94,234,212,0.3) 0%, rgba(45,180,166,0.14) 48%, rgba(94,234,212,0.2) 100%)',
    hoverBackground:
      'linear-gradient(180deg, rgba(94,234,212,0.42) 0%, rgba(45,180,166,0.2) 48%, rgba(94,234,212,0.28) 100%)',
    rimTop: 'rgba(190,255,244,0.9)',
    rimBottom: 'rgba(94,234,212,0.35)',
    glow: 'rgba(94,234,212,0.5)'
  },
  gold: {
    color: '#fff8e7',
    background:
      'linear-gradient(180deg, rgba(250,204,21,0.32) 0%, rgba(202,138,4,0.16) 48%, rgba(250,204,21,0.22) 100%)',
    hoverBackground:
      'linear-gradient(180deg, rgba(250,204,21,0.44) 0%, rgba(202,138,4,0.22) 48%, rgba(250,204,21,0.3) 100%)',
    rimTop: 'rgba(255,236,170,0.92)',
    rimBottom: 'rgba(250,204,21,0.35)',
    glow: 'rgba(250,204,21,0.5)'
  },
  ruby: {
    color: '#ffeef0',
    background:
      'linear-gradient(180deg, rgba(244,63,94,0.32) 0%, rgba(159,18,57,0.18) 48%, rgba(244,63,94,0.22) 100%)',
    hoverBackground:
      'linear-gradient(180deg, rgba(244,63,94,0.44) 0%, rgba(159,18,57,0.24) 48%, rgba(244,63,94,0.3) 100%)',
    rimTop: 'rgba(255,196,205,0.9)',
    rimBottom: 'rgba(244,63,94,0.35)',
    glow: 'rgba(244,63,94,0.48)'
  },
  obsidian: {
    color: '#f1f5f9',
    background:
      'linear-gradient(180deg, rgba(30,41,59,0.55) 0%, rgba(2,6,23,0.4) 48%, rgba(30,41,59,0.5) 100%)',
    hoverBackground:
      'linear-gradient(180deg, rgba(51,65,85,0.6) 0%, rgba(2,6,23,0.46) 48%, rgba(51,65,85,0.55) 100%)',
    rimTop: 'rgba(255,255,255,0.55)',
    rimBottom: 'rgba(148,163,184,0.25)',
    glow: 'rgba(148,163,184,0.3)'
  }
};

export const GlassButton = ({
  mode = 'light',
  tone = 'crystal',
  size = 'medium',
  sx,
  children,
  ...props
}: GlassButtonProps) => {
  const tokens = getDesignTokens(mode);
  const t = toneStyles[tone];

  return (
    <Button
      disableElevation
      size={size}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        minHeight: size === 'small' ? 42 : size === 'large' ? 58 : 50,
        px: size === 'small' ? 2.6 : size === 'large' ? 4.4 : 3.5,
        border: 'none',
        borderRadius: 999,
        color: t.color,
        background: t.background,
        backdropFilter: 'blur(26px) saturate(190%) brightness(1.06)',
        WebkitBackdropFilter: 'blur(26px) saturate(190%) brightness(1.06)',
        // Rim lighting: bright top edge, soft dark bottom edge, ambient drop
        boxShadow: [
          `inset 0 1.5px 0 0 ${t.rimTop}`,
          `inset 0 -1px 0 0 ${t.rimBottom}`,
          'inset 1px 0 0 0 rgba(255,255,255,0.18)',
          'inset -1px 0 0 0 rgba(255,255,255,0.18)',
          `inset 0 -12px 24px -14px ${t.glow}`,
          '0 10px 32px rgba(0,0,0,0.28)',
          '0 2px 6px rgba(0,0,0,0.2)'
        ].join(', '),
        fontSize: size === 'small' ? 14 : size === 'large' ? 19 : 16,
        fontWeight: 850,
        lineHeight: 1.1,
        letterSpacing: 0,
        textTransform: 'none',
        textShadow: '0 1px 2px rgba(0,0,0,0.25)',
        transition:
          'transform 180ms cubic-bezier(0.2,0.9,0.3,1.4), background 200ms ease, box-shadow 200ms ease',
        // Curved specular highlight across the top half (lens-like)
        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: -1,
          top: '6%',
          left: '4%',
          right: '4%',
          height: '46%',
          borderRadius: 999,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.08) 78%, rgba(255,255,255,0) 100%)',
          filter: 'blur(1px)',
          pointerEvents: 'none'
        },
        // Sheen that sweeps across on hover
        '&::after': {
          content: '""',
          position: 'absolute',
          zIndex: -1,
          top: '-60%',
          bottom: '-60%',
          left: '-90%',
          width: '55%',
          background:
            'linear-gradient(105deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.34) 50%, rgba(255,255,255,0) 100%)',
          transform: 'skewX(-18deg)',
          transition: 'left 520ms ease',
          pointerEvents: 'none'
        },
        '&:hover': {
          background: t.hoverBackground,
          transform: 'translateY(-1.5px)',
          boxShadow: [
            `inset 0 1.5px 0 0 ${t.rimTop}`,
            `inset 0 -1px 0 0 ${t.rimBottom}`,
            'inset 1px 0 0 0 rgba(255,255,255,0.24)',
            'inset -1px 0 0 0 rgba(255,255,255,0.24)',
            `inset 0 -12px 26px -12px ${t.glow}`,
            '0 16px 44px rgba(0,0,0,0.34)',
            '0 3px 8px rgba(0,0,0,0.22)'
          ].join(', '),
          '&::after': { left: '135%' }
        },
        '&:active': {
          transform: 'translateY(0.5px) scale(0.98)',
          boxShadow: [
            `inset 0 1px 0 0 ${t.rimTop}`,
            `inset 0 -1px 0 0 ${t.rimBottom}`,
            'inset 0 6px 18px rgba(0,0,0,0.18)',
            '0 6px 18px rgba(0,0,0,0.24)'
          ].join(', ')
        },
        '&:focus-visible': {
          boxShadow: `${tokens.shadow.focus}, inset 0 1.5px 0 0 ${t.rimTop}`
        },
        '&.Mui-disabled': {
          color: 'rgba(255,255,255,0.4)',
          background: 'rgba(255,255,255,0.05)',
          boxShadow:
            'inset 0 1px 0 0 rgba(255,255,255,0.14), inset 0 -1px 0 0 rgba(255,255,255,0.06)',
          textShadow: 'none',
          '&::before': { opacity: 0.3 },
          '&::after': { display: 'none' }
        },
        ...sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};
